import { createStore, createHook, createActionsHook, createStateHook } from 'react-sweet-state';

export type ParentContent = {
	id: string;
	title: string;
	contentType: string;
};

export type Content = {
	id: string;
	type: string;
	subType?: 'live' | null;
};

export type SubmitCallback = (props: { baseUrl: string; path: string }) => Promise<void>;

/**
 * Pre-fetched xFlow Jira data passed to SmartCreatePromptOnboarding.
 * Mirrors the `XFlowJiraData` type from
 * `@atlassian/conversation-assistant` without introducing a cross-package
 * dependency from the store.
 */
export type XFlowJiraData = {
	issue: {
		key: string | null;
		summary: string | null;
		webUrl: string | null;
	};
	project: {
		key: string | null;
		name: string | null;
		webUrl: string | null;
	};
	hasFullData: boolean;
};

/**
 * Raw onboarding context for the AIFC onboarding first screen.
 * Grouping these into a single object avoids prop-drilling five separate
 * fields through the modal stack.
 */
export type OnboardingData = {
	agentId?: string;
	contentType?: string;
	teamType?: string;
	isXFlowUser?: boolean;
	xFlowJiraData?: XFlowJiraData;
	/** Pre-formatted TWC prompt options to pass directly to the platform SmartCreationModal. */
	overridePromptOptions?: Array<{
		id: string;
		title: string;
		prompt: string;
		promptRegex: RegExp;
		imageType: 'page' | 'flowchart';
	}>;
	/** Entry point used to open the modal; consumed by platform-side onboarding UI to branch on TWC AIFC flow. */
	entryPoint?: string;
};

/**
 * Metadata for a Loom video attached to the smart-creation prompt. Mirrors
 * `LoomVideoMetadata` from `@confluence/smart-create` (a subset of the
 * @loomhq/record-sdk shape) without introducing a cross-package dependency
 * from the store. Persisted here so an SPA route change mid-recording — which
 * remounts the host modal — doesn't drop the attachment when the recorder's
 * orphaned "Add to prompt" callback fires after the remount.
 */
export type AttachedLoomVideo = {
	id: string;
	title: string;
	sharedUrl: string;
	embedUrl: string;
	thumbnailUrl?: string;
	thumbnailWidth?: number;
	thumbnailHeight?: number;
	duration?: number;
	description?: string;
	height: number;
	width: number;
};

export type OnCloseCallback = () => void;
export type OnOpenCallback = () => void;

export type OutcomeType = 'liveDoc' | 'page' | 'whiteboard' | 'database' | 'slides';

type State = {
	showSmartCreationModal: boolean;
	/**
	 * True while the Loom recorder takes over the screen and the modal is
	 * hidden behind it. Lives in this remount-surviving store so an SPA route
	 * change mid-recording doesn't reset it and pop the modal back into view.
	 */
	isLoomRecorderOpen: boolean;
	/**
	 * The Loom video attached to the prompt, or null when none is attached.
	 * Lives in this remount-surviving store (rather than modal-local state) so
	 * the recorder's "Add to prompt" callback — captured by the SDK from the
	 * pre-navigation modal instance — still lands on the live modal after an
	 * SPA route change remounts it.
	 */
	attachedLoomVideo: AttachedLoomVideo | null;
	spaceKey: string;
	parentContent: ParentContent;
	content?: Content;
	stagingAreaActive: boolean;
	isFloating: boolean;
	shouldShowBackButton?: boolean;
	entryPoint: string;
	initialOutcomeType?: OutcomeType;
	/**
	 * When `true`, the `initialOutcomeType` pill is locked for the session
	 * (cross hidden, click no-op). Opt-in per entrypoint — e.g. set by the
	 * `cwr_blank_object_experiment` variant 3 entrypoints, left `false` by
	 * Slides.
	 */
	lockInitialOutcomeType: boolean;
	remixConversationId: string | null;
	/**
	 * Grouped onboarding context for the AIFC onboarding first screen.
	 * Undefined when the modal is not opened from the onboarding flow.
	 */
	onboardingData?: OnboardingData;
	/** Called after the onboarding prompt is submitted. No args — advances the step. */
	onOnboardingSubmit?: () => void;
	/**
	 * Total number of steps in the onboarding flow. Used to render the step
	 * indicator dots in the modal header dynamically.
	 */
	totalSteps?: number;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
	onOpenCallback?: OnOpenCallback;
};

const initialState: State = {
	showSmartCreationModal: false,
	isLoomRecorderOpen: false,
	attachedLoomVideo: null,
	spaceKey: '',
	parentContent: {
		id: '',
		title: '',
		contentType: '',
	},
	content: undefined,
	stagingAreaActive: false,
	isFloating: false,
	shouldShowBackButton: undefined,
	entryPoint: 'confluenceUnknown',
	initialOutcomeType: undefined,
	lockInitialOutcomeType: false,
	remixConversationId: null,
	onboardingData: undefined,
	onOnboardingSubmit: undefined,
	totalSteps: undefined,
	submitCallback: undefined,
	onCloseCallback: undefined,
	onOpenCallback: undefined,
};

export type OpenModalParams = {
	entryPoint: string;
	spaceKey?: string;
	parentContent?: ParentContent;
	stagingAreaActive?: boolean;
	isFloating?: boolean;
	shouldShowBackButton?: boolean;
	content?: Content;
	initialOutcomeType?: OutcomeType;
	/** See `State.lockInitialOutcomeType`. */
	lockInitialOutcomeType?: boolean;
	remixConversationId?: string;
	/** Grouped onboarding context for the AIFC onboarding first screen. */
	onboardingData?: OnboardingData;
	/** Called after the onboarding prompt is submitted. No args — advances the step. */
	onOnboardingSubmit?: () => void;
	/** Total number of steps in the onboarding flow for the step indicator. */
	totalSteps?: number;
	submitCallback?: SubmitCallback;
	onCloseCallback?: OnCloseCallback;
	onOpenCallback?: OnOpenCallback;
};

export const actions = {
	openModal:
		({
			entryPoint,
			spaceKey,
			parentContent,
			stagingAreaActive,
			isFloating,
			shouldShowBackButton,
			content,
			initialOutcomeType,
			lockInitialOutcomeType,
			submitCallback,
			onCloseCallback,
			onOpenCallback,
			remixConversationId: remixConversationIdFromOpen,
			onboardingData,
			onOnboardingSubmit,
			totalSteps,
		}: OpenModalParams) =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			const floating = isFloating ?? false;
			const remixConversationId =
				floating &&
				remixConversationIdFromOpen !== undefined &&
				remixConversationIdFromOpen !== null &&
				remixConversationIdFromOpen !== ''
					? remixConversationIdFromOpen
					: null;
			setState({
				showSmartCreationModal: true,
				// A fresh open is never behind the recorder; clearing here also
				// recovers if a prior recorder session left the flag stuck on.
				isLoomRecorderOpen: false,
				// A fresh open never carries a prior session's attachment.
				attachedLoomVideo: null,
				entryPoint,
				spaceKey,
				parentContent: parentContent ?? {
					id: '',
					title: '',
					contentType: '',
				},
				content: content ?? undefined,
				stagingAreaActive: stagingAreaActive ?? false,
				isFloating: floating,
				shouldShowBackButton: shouldShowBackButton ?? undefined,
				initialOutcomeType: initialOutcomeType ?? undefined,
				// Lock requires an initial outcome type — guard against misuse.
				lockInitialOutcomeType: Boolean(initialOutcomeType && lockInitialOutcomeType),
				remixConversationId,
				onboardingData: onboardingData ?? undefined,
				onOnboardingSubmit: onOnboardingSubmit ?? undefined,
				totalSteps: totalSteps ?? undefined,
				submitCallback: submitCallback ?? undefined,
				onCloseCallback: onCloseCallback ?? undefined,
				onOpenCallback: onOpenCallback ?? undefined,
			});
		},
	closeModal:
		() =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			setState({
				showSmartCreationModal: false,
				isLoomRecorderOpen: false,
				attachedLoomVideo: null,
				content: undefined,
				stagingAreaActive: false,
				isFloating: false,
				shouldShowBackButton: undefined,
				entryPoint: 'confluenceUnknown',
				initialOutcomeType: undefined,
				lockInitialOutcomeType: false,
				remixConversationId: null,
				onboardingData: undefined,
				onOnboardingSubmit: undefined,
				onCloseCallback: undefined,
				onOpenCallback: undefined,
			});
		},
	setLoomRecorderOpen:
		(isLoomRecorderOpen: boolean) =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			setState({ isLoomRecorderOpen });
		},
	setAttachedLoomVideo:
		(attachedLoomVideo: AttachedLoomVideo | null) =>
		({ setState }: { setState: (state: Partial<State>) => void }) => {
			setState({ attachedLoomVideo });
		},
};

export const Store = createStore({
	initialState,
	actions,
	name: 'smartCreationModalStore',
});

export const useSmartCreationModalStore = createHook(Store);

export const useSmartCreationModalActions = createActionsHook(Store);
export const useSmartCreationModalState = createStateHook(Store);
